/**
 * The notification panel (spec §7.14). The records themselves are produced by
 * the platform outbox/event machinery; this screen reads them and marks one
 * read. The four categories are fixed by the event contract.
 */

import type { JsonFetch } from '../accounts/accounts.api';

export type { JsonFetch } from '../accounts/accounts.api';

export const NOTIFICATION_KINDS = [
  'approval',
  'deadline',
  'closing_warning',
  'integration_failure',
] as const;

export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export interface NotificationRecord {
  id: string;
  kind: NotificationKind;
  entity: string;
  entityId: string | null;
  payload: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}

export function unreadCount(records: readonly NotificationRecord[]): number {
  return records.filter((record) => record.readAt === null).length;
}

export function filterByKind(
  records: readonly NotificationRecord[],
  kind: NotificationKind | null,
): NotificationRecord[] {
  return kind === null ? [...records] : records.filter((record) => record.kind === kind);
}

function payloadPath(record: NotificationRecord): string | null {
  const path = record.payload.path;
  return typeof path === 'string' && path.startsWith('/') ? path : null;
}

/** Where the "open" action takes the user, or null when the record has no target. */
export function targetPath(record: NotificationRecord): string | null {
  const explicit = payloadPath(record);
  if (explicit !== null) {
    return explicit;
  }
  if (record.entity === 'document' && record.entityId !== null) {
    return `/documents/${record.entityId}`;
  }
  if (record.entity === 'period' || record.entity === 'closing') {
    return '/closing';
  }
  if (record.entity === 'outbox' || record.entity === 'integration' || record.entity === 'settings') {
    return '/settings';
  }
  return null;
}

export async function loadNotifications(fetchJson: JsonFetch): Promise<NotificationRecord[]> {
  return fetchJson<NotificationRecord[]>('/notifications');
}

export async function markNotificationRead(
  fetchJson: JsonFetch,
  id: string,
): Promise<NotificationRecord> {
  return fetchJson<NotificationRecord>(`/notifications/${encodeURIComponent(id)}/read`, {
    method: 'POST',
  });
}

export async function markAllNotificationsRead(fetchJson: JsonFetch): Promise<void> {
  await fetchJson<unknown>('/notifications/read-all', { method: 'POST' });
}
